import { Label } from "flowbite-react";
import {
  forwardRef,
  InputHTMLAttributes,
  useState,
  useRef,
  useEffect,
} from "react";
import {
  Control,
  Controller,
  FieldError,
  FieldErrorsImpl,
  Merge,
  useWatch,
} from "react-hook-form";
import * as FlagIcons from "country-flag-icons/react/3x2";
import { countries } from "../../utils/countries";
import * as libphonenumber from "google-libphonenumber";
import { HiChevronDown } from "react-icons/hi";
import { twMerge } from "tailwind-merge";

interface CustomPhoneNumberProps
  extends InputHTMLAttributes<HTMLInputElement | HTMLTextAreaElement> {
  name?: string;
  control?: Control<any>;
  error?:
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | string
    | boolean;
  helperText?:
    | string
    | FieldError
    | Merge<FieldError, FieldErrorsImpl<any>>
    | undefined;
  placeholder?: string;
  defaultValue?: string;
  label?: string;
  labelVariant?: "label" | "title" | "none";
  labelClassName?: string;
  rules?: any;
  onValueChange?: (e: any) => void;
  defaultCountryCode?: string;
}

const DEFAULT_COUNTRY_CODE = "US";

const findCountry = (code?: string) =>
  countries.find((x) => x.code === (code || DEFAULT_COUNTRY_CODE));

const CountryFlag = ({ countryCode }: { countryCode: string }) => {
  const FlagComponent = (FlagIcons as any)[countryCode];

  if (!FlagComponent) {
    return null;
  }

  return <FlagComponent className="h-4 w-4 me-2" translate="no" />;
};

export const CustomPhoneNumber = forwardRef<
  HTMLInputElement | HTMLTextAreaElement,
  CustomPhoneNumberProps
>(
  (
    {
      name = "",
      control,
      error,
      helperText,
      className = "",
      placeholder = "",
      type = "text",
      defaultValue,
      label,
      labelVariant = "label",
      labelClassName,
      rules,
      onValueChange,
      defaultCountryCode,
      ...rest
    },
    ref
  ) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedCountry, setSelectedCountry] = useState<{
      name: string;
      dial_code: string;
      code: string;
    }>();
    const [phoneNumber, setPhoneNumber] = useState("");
    const [highlightedIndex, setHighlightedIndex] = useState<number>(-1);
    const [dropdownPosition, setDropdownPosition] = useState<{
      top: number;
      left: number;
      width: number;
    }>({ top: 0, left: 0, width: 0 });
    const dropdownRef = useRef<HTMLDivElement>(null);
    const buttonRef = useRef<HTMLButtonElement>(null);
    const searchTimeoutRef = useRef<NodeJS.Timeout>();
    const highlightedItemRef = useRef<HTMLLIElement>(null);
    const searchTermRef = useRef<string>("");
    const lastParsedValue = useRef<string>("");
    const hasSetDefaultCountry = useRef(false);
    const countrySetFromPhoneNumber = useRef(false);
    const phoneUtil = libphonenumber.PhoneNumberUtil.getInstance();

    const fieldValue = useWatch({
      control,
      name,
      defaultValue: defaultValue ?? "",
    });

    useEffect(() => {
      if (fieldValue) {
        if (fieldValue !== lastParsedValue.current) {
          parseAndSetPhoneNumber(fieldValue);
        }
        return;
      }

      lastParsedValue.current = "";
      if (!phoneNumber) return;
      setPhoneNumber("");
      countrySetFromPhoneNumber.current = false;
      const fallbackCountry = findCountry(defaultCountryCode);
      if (fallbackCountry) setSelectedCountry(fallbackCountry);
    }, [fieldValue, phoneNumber, defaultCountryCode]);

    useEffect(() => {
      if (!countrySetFromPhoneNumber.current) {
        const selected = findCountry(defaultCountryCode);
        if (selected) setSelectedCountry(selected);
      }
    }, [defaultCountryCode]);

    useEffect(() => {
      if (isOpen && buttonRef.current) {
        const rect = buttonRef.current.getBoundingClientRect();
        setDropdownPosition({
          top: rect.height + (label && label !== "" ? 24 : 0),
          left: 0,
          width: Math.min(rect.width * 3, 400),
        });
      }
    }, [isOpen, label]);

    useEffect(() => {
      if (!isOpen) return;

      const handleClickOutside = (event: MouseEvent) => {
        const target = event.target as Node;
        if (
          dropdownRef.current &&
          !dropdownRef.current.contains(target) &&
          buttonRef.current &&
          !buttonRef.current.contains(target)
        ) {
          setIsOpen(false);
          setHighlightedIndex(-1);
        }
      };

      const timeoutId = setTimeout(() => {
        document.addEventListener("mousedown", handleClickOutside);
      }, 0);

      return () => {
        clearTimeout(timeoutId);
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, [isOpen]);

    useEffect(() => {
      if (!isOpen) return;

      const handleKeyPress = (event: KeyboardEvent) => {
        if (event.key && event.key.length === 1 && /[a-zA-Z]/.test(event.key)) {
          const newSearchTerm = searchTermRef.current + event.key.toLowerCase();
          searchTermRef.current = newSearchTerm;

          const matchIndex = countries.findIndex((country) =>
            country.name.toLowerCase().startsWith(newSearchTerm)
          );

          if (matchIndex !== -1) {
            setHighlightedIndex(matchIndex);
          }

          if (searchTimeoutRef.current) {
            clearTimeout(searchTimeoutRef.current);
          }
          searchTimeoutRef.current = setTimeout(() => {
            searchTermRef.current = "";
          }, 1000);
        }
      };

      document.addEventListener("keydown", handleKeyPress);
      return () => {
        document.removeEventListener("keydown", handleKeyPress);
        if (searchTimeoutRef.current) {
          clearTimeout(searchTimeoutRef.current);
        }
      };
    }, [isOpen]);

    useEffect(() => {
      if (highlightedIndex !== -1 && highlightedItemRef.current) {
        highlightedItemRef.current.scrollIntoView({
          behavior: "smooth",
          block: "nearest",
        });
      }
    }, [highlightedIndex]);

    const applyDialCodeFallback = (value: string): boolean => {
      if (!value.startsWith("+")) return false;
      const matches = countries.filter((c) => value.startsWith(c.dial_code));
      if (matches.length === 0) return false;
      const longestDialCodeLength = Math.max(
        ...matches.map((c) => c.dial_code.length)
      );
      const narrowed = matches.filter(
        (c) => c.dial_code.length === longestDialCodeLength
      );
      const matchedCountry =
        narrowed.find((c) => c.code === "US") || narrowed[0];
      setSelectedCountry(matchedCountry);
      setPhoneNumber(value.slice(matchedCountry.dial_code.length));
      hasSetDefaultCountry.current = true;
      countrySetFromPhoneNumber.current = true;
      return true;
    };

    const parseAndSetPhoneNumber = (value: string) => {
      if (!value || value === lastParsedValue.current) return false;

      lastParsedValue.current = value;
      let countryWasSet = false;

      try {
        const parsedNumber = phoneUtil.parse(value);
        const countryCode = phoneUtil.getRegionCodeForNumber(parsedNumber);
        const country = countryCode
          ? countries.find((c) => c.code === countryCode)
          : undefined;

        if (country) {
          setSelectedCountry(country);
          countryWasSet = true;
          hasSetDefaultCountry.current = true;
          countrySetFromPhoneNumber.current = true;

          const nationalNumber = phoneUtil.format(
            parsedNumber,
            libphonenumber.PhoneNumberFormat.NATIONAL
          );
          const cleanNumber = nationalNumber.replace(/[\s\-()]/g, "");
          setPhoneNumber(cleanNumber);
        } else {
          countryWasSet = applyDialCodeFallback(value);
        }
      } catch (error) {
        countryWasSet = applyDialCodeFallback(value);
      }

      return countryWasSet;
    };

    const formatToE164 = (phoneInput: string, countryCode: string): string => {
      try {
        const number = phoneUtil.parse(phoneInput, countryCode);
        const e164 = phoneUtil.format(
          number,
          libphonenumber.PhoneNumberFormat.E164
        );
        return e164;
      } catch (error) {
        return selectedCountry?.dial_code + phoneInput;
      }
    };

    const handleCountrySelect = (
      country: (typeof countries)[0],
      onChange: (value: string) => void
    ) => {
      setSelectedCountry(country);
      setHighlightedIndex(-1);

      setTimeout(() => {
        setIsOpen(false);
      }, 0);

      if (!phoneNumber || phoneNumber.trim() === "") {
        onChange("");
        return;
      }

      const e164Value = formatToE164(phoneNumber, country.code);
      onChange(e164Value);
    };

    const handlePhoneChange = (
      e: React.ChangeEvent<HTMLInputElement>,
      onChange: (value: string) => void
    ) => {
      const value = e.target.value;
      setPhoneNumber(value);

      if (!value || value.trim() === "") {
        onChange("");
        return;
      }

      const e164Value = formatToE164(value, selectedCountry?.code as string);
      onChange(e164Value);
    };

    return (
      <div className="flex flex-row gap-1 relative">
        <div className="w-full space-y-1" dir="auto">
          {label && (
            <Label
              htmlFor={name}
              className={twMerge(
                labelVariant === "none"
                  ? ""
                  : labelVariant === "label"
                  ? "block text-sm font-medium text-gray-700"
                  : "block mb-3 text-sm font-medium text-gray-700",
                labelClassName
              )}
            >
              {label}
              {rest.required && (
                <span className="ml-0.5 text-gray-400" aria-hidden="true">
                  *
                </span>
              )}
            </Label>
          )}

          <div className="flex flex-col w-full">
            <Controller
              name={name}
              control={control}
              defaultValue={defaultValue ?? ""}
              rules={rules}
              render={({ field }) => {
                return (
                  <>
                    <div className="relative flex items-center">
                      <button
                        translate="no"
                        ref={buttonRef}
                        onClick={() => setIsOpen(!isOpen)}
                        className="shrink-0 z-10 inline-flex items-center py-2.5 md:py-2 px-2 text-base md:text-sm font-medium text-center text-gray-900 bg-gray-50 border border-gray-300 rounded-s-md hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:bg-gray-700 dark:hover:bg-gray-600 dark:text-white dark:border-gray-600"
                        type="button"
                      >
                        <CountryFlag
                          countryCode={selectedCountry?.code as string}
                        />
                        {selectedCountry?.dial_code}{" "}
                        <HiChevronDown size={14} className="ml-2" />
                      </button>

                      <div className="relative w-full">
                        <input
                          {...field}
                          value={phoneNumber}
                          onChange={(e) => handlePhoneChange(e, field.onChange)}
                          type="text"
                          id="phone-input"
                          className={twMerge(
                            "block p-2.5 md:py-2 w-full z-20 text-base md:text-sm text-gray-900 bg-white rounded-e-md border-s-0 border",
                            error
                              ? "border-red-500 focus:border-red-500 focus:ring-red-500"
                              : "border-gray-300 focus:border-blue-500 focus:ring-blue-500",
                            className
                          )}
                          maxLength={13}
                          placeholder={placeholder || ""}
                        />
                      </div>
                    </div>

                    {isOpen && (
                      <div
                        ref={dropdownRef}
                        className="absolute z-[9999] bg-white divide-y divide-gray-100 rounded-lg shadow-lg border border-gray-200 dark:bg-gray-700 dark:border-gray-600"
                        style={{
                          top: `${dropdownPosition.top}px`,
                          left: `${dropdownPosition.left}px`,
                          width: `${dropdownPosition.width}px`,
                          maxWidth: "400px",
                        }}
                      >
                        <ul className="max-h-[250px] overflow-auto py-2 text-sm text-gray-700 dark:text-gray-200">
                          {countries.map((country, index) => {
                            const isHighlighted = index === highlightedIndex;
                            return (
                              <li
                                key={index}
                                ref={isHighlighted ? highlightedItemRef : null}
                              >
                                <button
                                  onClick={() =>
                                    handleCountrySelect(country, field.onChange)
                                  }
                                  type="button"
                                  className={`inline-flex w-full px-4 py-2 text-sm items-center gap-2 ${
                                    isHighlighted
                                      ? "bg-blue-100 text-gray-900 dark:bg-blue-600 dark:text-white"
                                      : "text-gray-700 hover:bg-gray-100 dark:text-gray-200 dark:hover:bg-gray-600 dark:hover:text-white"
                                  }`}
                                  role="menuitem"
                                >
                                  <div className="shrink-0">
                                    <CountryFlag countryCode={country.code} />
                                  </div>
                                  <span className="truncate text-left flex-1 min-w-0">
                                    {country.name}
                                  </span>
                                  <span className="shrink-0 text-left">
                                    {country.dial_code}
                                  </span>
                                </button>
                              </li>
                            );
                          })}
                        </ul>
                      </div>
                    )}
                  </>
                );
              }}
            />
          </div>

          {helperText && (
            <p dir="auto" className="text-xs text-red-600">
              {typeof helperText === "string"
                ? helperText?.toString()
                : helperText?.message?.toString() ?? ""}
            </p>
          )}
        </div>
      </div>
    );
  }
);

CustomPhoneNumber.displayName = "CustomPhoneNumber";
